/* eslint-disable i18next/no-literal-string */
import { type BankDetailsInfo, type FullTradeCompany, isObject, useApiAccountsV1BankDetailsList } from 'api';
import { useState } from 'react';
import type { AutocompleteProps } from 'ui';

import { AutocompleteField } from '../AutocompleteField/AutocompleteField';

export type BankDetailsAutocompleteProps = {
  name: string;
  label?: string;
  required?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
  tradeCompanies?: FullTradeCompany['id'][];
  onSelectOption?: (value: BankDetailsInfo) => void;
  defaultValue?: BankDetailsInfo['id'];
} & Omit<AutocompleteProps<BankDetailsInfo>, 'options' | 'defaultValue'>;

export const BankDetailsAutocomplete = ({
  name,
  label = 'Select company',
  size,
  required,
  onSelectOption,
  disabled = false,
  tradeCompanies,
  defaultValue,
  ...rest
}: BankDetailsAutocompleteProps) => {
  const [query, setQuery] = useState(defaultValue?.toString() || '');

  const { data, isLoading } = useApiAccountsV1BankDetailsList({
    search: query,
    trade_companies: tradeCompanies,
  });

  return (
    <AutocompleteField<BankDetailsInfo>
      options={data?.results || []}
      loading={isLoading}
      getOptionLabel={(item) => {
        if (isObject(item) && item?.name) {
          return item.name;
        }
        return '';
      }}
      label={label}
      name={name}
      isOptionEqualToValue={(option, value) => {
        return value === undefined || option?.id?.toString() === (value?.id ?? value)?.toString();
      }}
      placeholder="Type to search"
      valueField="id"
      size={size}
      required={required}
      data-testid="bank-details-autocomplete"
      onSelectOption={onSelectOption}
      disabled={disabled}
      filterOptions={(options, state) => {
        setQuery(state.inputValue);
        return options;
      }}
      {...rest}
    />
  );
};
